import React, { useContext } from 'react';

import { useChildComponent } from '@gogame/hooks-ts';
import DataGrid from '@gogame/data-grid';
import { ScreenGo, useScreenTitle } from '@gogame/screens';
import { Stack, Tags } from '@gogame/components';

import { DIMENSION_REAL_CONVERSION_PAGE } from 'scenes/Trackers/Tracker/constants';

import { PreviewFile } from 'scenes/Gallery/components/File';
import { AttributeItem } from 'scenes/Gallery/components/AttributeList';

import ConversionPageActions from 'scenes/Gallery/ConversionPages/ConversionPages/Actions';
import ConversionPageFormEdit from 'scenes/Gallery/ConversionPages/ConversionPages/ConversionPageForm';

import ConversionPagesDataTableContext from './ConversionPagesDataTableContext';
import { BatchToolbarDataGrid } from './components/BatchToolbar';
import Toolbar from './components/Toolbar';
import {
  COLUMN_ID,
  COLUMN_PREVIEW,
  COLUMN_NAME,
  COLUMN_APPLICATION,
  COLUMN_DOMAIN,
  COLUMN_LOCALE,
  COLUMN_FLOW,
  COLUMN_PLATFORM,
  COLUMN_USED_IN_ADS,
  COLUMN_TAGS,
  COLUMN_CREATED_DATE,
} from './constants';
import AdsLink from '../AdsLink';

const ConversionPagesDetailsLink = ({ children, row }) => (
  <ScreenGo to={`${row.id}`}>{children}</ScreenGo>
);

const ConversionPagesDataTable = ({ height, tabs, withTitle }) => {
  const {
    data,
    isLoading,
    isRefetching,
    tableSettings,
    columns,
    onReorder,
    onCollapse,
    onChangeSettings,
  } = useContext(ConversionPagesDataTableContext);

  const [screenTitle] = useScreenTitle();

  const [childComponent, openChildComponent] = useChildComponent();

  const handleEdit = ({ id }) => {
    openChildComponent(ConversionPageFormEdit, { id });
  };

  return (
    <React.Fragment>
      <DataGrid
        rows={data?.rows}
        count={data?.count}
        title={screenTitle}
        loading={isLoading}
        fetching={isRefetching}
        onChangeSettings={onChangeSettings}
        onReorder={onReorder}
        onCollapse={onCollapse}
        tableSettings={tableSettings}
        columnsState={columns}
        checkboxSelection={true}
        toolbar={<Toolbar tabs={tabs} withTitle={withTitle} />}
        selectedRowsToolbarActions={({ selectedValues }) => (
          <BatchToolbarDataGrid
            selectedValues={selectedValues}
            openChildComponent={openChildComponent}
          />
        )}
        storageKey={'conversion-pages'}
        defaultColumnWidth={140}
        rowHeight={80}
        minHeight={height}
        notInitedLabel={'No records'}
      >
        <DataGrid.Column
          field={COLUMN_ID}
          type={'text'}
          headerName={'ID'}
          width={90}
          fixed={true}
          resizable={false}
          align={'right'}
          headerAlign={'right'}
        />
        <DataGrid.Column
          field={COLUMN_PREVIEW}
          headerName={'Preview'}
          width={130}
          fixed={true}
          resizable={false}
          sortable={false}
          formatter={(value, row) => (
            <ConversionPagesDetailsLink row={row}>
              <PreviewFile
                src={value}
                width={109}
                height={64}
                heightCover={true}
              />
            </ConversionPagesDetailsLink>
          )}
        />
        <DataGrid.Column
          field={COLUMN_NAME}
          type={'link'}
          headerName={'Name'}
          width={460}
          linkComponent={ConversionPagesDetailsLink}
          getTooltip={(v) => v}
        />
        <DataGrid.Column
          field={COLUMN_APPLICATION}
          headerName={'Application'}
          getValue={(v) => v?.name}
        />
        <DataGrid.Column field={COLUMN_DOMAIN} headerName={'Domain'} />
        <DataGrid.Column
          field={COLUMN_LOCALE}
          headerName={'Locale'}
          width={112}
        />
        <DataGrid.Column
          field={COLUMN_FLOW}
          headerName={'Flow'}
          width={100}
          getValue={(v) => v?.name}
        />
        <DataGrid.Column
          field={COLUMN_PLATFORM}
          headerName={'Platform'}
          width={122}
          getValue={(v) => v?.name}
        />
        <DataGrid.Column
          field={COLUMN_USED_IN_ADS}
          headerName={'Used in Ads'}
          width={80}
          formatter={(value, row) => {
            if (value) {
              return (
                <AdsLink
                  dimension={DIMENSION_REAL_CONVERSION_PAGE}
                  id={row.name}
                >
                  <AttributeItem
                    type={'label'}
                    valueParser={'ads'}
                    clickable={true}
                    underline={'always'}
                    typographyVariant={'body2'}
                  />
                </AdsLink>
              );
            }
          }}
        />
        <DataGrid.Column
          field={COLUMN_TAGS}
          headerName={'Tags'}
          width={260}
          sortable={false}
          renderCell={({ value, colDef: { width } }) => {
            return value?.length ? (
              <Stack
                direction={'row'}
                alignItems={'center'}
                height={'100%'}
                lineHeight={'normal'}
              >
                <Tags
                  data={value}
                  getTagLabel={({ name }) => name}
                  width={width}
                  lineClamp={2}
                />
              </Stack>
            ) : (
              '-'
            );
          }}
        />
        <DataGrid.Column
          field={COLUMN_CREATED_DATE}
          headerName={'Created'}
          type={'date'}
          timeZone={'local'}
          template={'LLL'}
        />
        <DataGrid.Column
          actionsColDef
          type={'actions'}
          actionsComponent={ConversionPageActions}
          onEdit={handleEdit}
          limit={3}
        />
      </DataGrid>
      {childComponent}
    </React.Fragment>
  );
};
export default ConversionPagesDataTable;
